import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { RootState } from '../store';
import { updateTokens, logout } from '../features/auth/redux/authSlice';
import { AUTH_API_URLS } from '../features/auth/constants';

interface BaseQueryWithReauthConfig {
  baseUrl: string;
  additionalHeaders?: Record<string, string>;
}

// Import the baseAuthQuery to avoid duplication
const baseAuthQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_UAT_BASE_AUTH_URL,
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState;
    // Note: Don't add authorization header for auth requests to avoid conflicts
    // Custom headers
    headers.set('Content-type', 'application/json');
    headers.set('Source-App', 'DEV'); // should be QdosV5
    headers.set('Source-Remote-Ip', '127.0.0.1');
    headers.set('Source-Local-Ip', '127.0.0.1');
    return headers;
  },
});

export const createBaseQueryWithReauth = ({
  baseUrl,
  additionalHeaders = {},
}: BaseQueryWithReauthConfig): BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> => {
  const baseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const state = getState() as RootState;
      const token = state.auth?.accessToken;

      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }

      // Add standard headers
      headers.set('Content-type', 'application/json');
      headers.set('Source-App', 'DEV'); // should be QdosV5
      headers.set('Source-Remote-Ip', '127.0.0.1');
      headers.set('Source-Local-Ip', '127.0.0.1');

      return headers;
    },
  });

  return async (args, api, extraOptions) => {

    let result = await baseQuery(args, api, extraOptions);

    // Check if the result is an error with status 401 (Unauthorized) or 403 (Forbidden)
    if (result.error) {
      // Handle both explicit auth errors (401/403) and FETCH_ERROR (which can mask auth errors due to CORS)
      const isAuthError =
        result.error.status === 401 ||
        result.error.status === 403 ||
        (result.error.status === 'FETCH_ERROR' &&
          result.error.error === 'TypeError: Failed to fetch');

      if (isAuthError) {

        const state = api.getState() as RootState;
        const refreshToken = state.auth?.refreshToken;
        const accessToken = state.auth?.accessToken;

        
        if (refreshToken) {
          try {

            // Try to get a new access token using the shared baseAuthQuery
            const refreshResult = await baseAuthQuery(
              {
                url: AUTH_API_URLS.REFRESH_TOKEN,
                method: 'POST',
                body: {
                  'refresh-token': refreshToken,
                  'access-token': accessToken,
                },
              },
              api,
              extraOptions
            );


            if (refreshResult.data) {
              const refreshData = refreshResult.data as any;
              
              // Check if the response has the expected structure
              if (
                refreshData.data &&
                refreshData.data.accessToken &&
                refreshData.data.refreshToken
              ) {
                // console.log(
                //   '✅ Valid token structure found, updating tokens...'
                // );

                // Update the tokens in the store
                api.dispatch(
                  updateTokens({
                    accessToken: refreshData.data.accessToken,
                    refreshToken: refreshData.data.refreshToken,
                  })
                );

                // console.log('🔄 Tokens updated, retrying original request...');

                // Retry the original request with the new token
                result = await baseQuery(args, api, extraOptions);
                // console.log('🔄 Retry result:', {
                //   data: result.data,
                //   error: result.error,
                //   meta: result.meta,
                // });
              } else {
                console.warn(
                  '⚠️ Invalid refresh token response structure:',
                  refreshData
                );
                throw new Error('Invalid refresh token response');
              }
            } else {
              // console.log('❌ Token refresh failed:', refreshResult.error);

              // If refresh also failed with FETCH_ERROR, it might be a CORS issue
              if (refreshResult.error?.status === 'FETCH_ERROR') {
                // console.log(
                //   '⚠️ Refresh failed with FETCH_ERROR - likely CORS issue. Check server CORS configuration.'
                // );
              }

              throw new Error('Token refresh failed');
            }
          } catch (error) {
            // console.error('💥 Error during token refresh:', error);
            // console.log('🚪 Token refresh failed, logging out user...');

            // If refresh token is also expired or request failed, logout the user
            api.dispatch(logout());

            // Optionally redirect to login page
            window.location.href = '/login';
          }
        } else {
          // console.log('❌ No refresh token available, logging out user...');

          // No refresh token available, logout the user
          api.dispatch(logout());
          window.location.href = '/login';
        }
      } else {
        // console.log('❌ Non-auth error:', result.error.status, result.error);
      }
    } else {
      // console.log('✅ Request successful');
    }

    return result;
  };
};
